import { HttpErrorResponse } from "@angular/common/http";
import { createReducer, on } from "@ngrx/store";
import {
  ForumAttachment,
  ForumAttachmentConfig,
  ForumMember,
  ForumOverviewGroup,
  ForumPost,
  ForumPostReaction,
  ForumReactionOption,
  ForumReactionSummary,
  ForumThreadRead,
  ForumThread,
  ForumThreadType,
} from "src/app/shared/interfaces/forum";
import {
  forumAttachmentConfigLoad,
  forumAttachmentConfigLoadFailure,
  forumAttachmentConfigLoadSuccess,
  forumAttachmentCreate,
  forumAttachmentCreateFailure,
  forumAttachmentCreateSuccess,
  forumAttachmentDelete,
  forumAttachmentDeleteFailure,
  forumAttachmentDeleteSuccess,
  forumAttachmentsLoad,
  forumAttachmentsLoadFailure,
  forumAttachmentsLoadSuccess,
  forumPostCreate,
  forumPostAttachmentsUploadFinished,
  forumPostCreateFailure,
  forumPostCreateSuccess,
  forumPostDelete,
  forumPostDeleteFailure,
  forumPostDeleteSuccess,
  forumPostReactionCreate,
  forumPostReactionCreateFailure,
  forumPostReactionChanged,
  forumPostReactionCreateSuccess,
  forumPostReactionDelete,
  forumPostReactionDeleteFailure,
  forumPostReactionDeleteSuccess,
  forumPostReactionsLoad,
  forumPostReactionsLoadFailure,
  forumPostReactionsLoadSuccess,
  forumMembersLoad,
  forumMembersLoadFailure,
  forumMembersLoadSuccess,
  forumOverviewGroupsLoad,
  forumOverviewGroupsLoadFailure,
  forumOverviewGroupsLoadSuccess,
  forumPostsLoad,
  forumPostsLoadFailure,
  forumPostsLoadSuccess,
  forumPostUpdate,
  forumPostUpdateFailure,
  forumPostUpdateSuccess,
  forumThreadCreate,
  forumThreadCreateFailure,
  forumThreadCreateSuccess,
  forumThreadDelete,
  forumThreadDeleteFailure,
  forumThreadDeletePernament,
  forumThreadDeletePernamentFailure,
  forumThreadDeletePernamentSuccess,
  forumThreadDeleteSuccess,
  forumThreadDetailsLoad,
  forumThreadDetailsLoadFailure,
  forumThreadDetailsLoadSuccess,
  forumThreadsLoad,
  forumThreadsLoadFailure,
  forumThreadsReset,
  forumThreadsLoadSuccess,
  forumThreadUpdate,
  forumThreadUpdateFailure,
  forumThreadUpdateSuccess,
  forumThreadAttachmentCreate,
  forumThreadAttachmentCreateFailure,
  forumThreadAttachmentCreateSuccess,
  forumThreadAttachmentDelete,
  forumThreadAttachmentDeleteFailure,
  forumThreadAttachmentDeleteSuccess,
  forumThreadAttachmentsLoad,
  forumThreadAttachmentsLoadFailure,
  forumThreadAttachmentsLoadSuccess,
  forumThreadFavorite,
  forumThreadFavoriteFailure,
  forumThreadFavoriteSuccess,
  forumThreadReactionCreate,
  forumThreadReactionCreateFailure,
  forumThreadReactionCreateSuccess,
  forumThreadReactionChanged,
  forumThreadReactionDelete,
  forumThreadReactionDeleteFailure,
  forumThreadReactionDeleteSuccess,
  forumThreadReactionDetailsLoad,
  forumThreadReactionDetailsLoadFailure,
  forumThreadReactionDetailsLoadSuccess,
  forumThreadReadsLoad,
  forumThreadReadsLoadFailure,
  forumThreadReadsLoadSuccess,
  forumThreadPin,
  forumThreadPinFailure,
  forumThreadPinSuccess,
  forumThreadRestore,
  forumThreadRestoreFailure,
  forumThreadRestoreSuccess,
  forumThreadUnpin,
  forumThreadUnpinFailure,
  forumThreadUnpinSuccess,
  forumThreadUnfavorite,
  forumThreadUnfavoriteFailure,
  forumThreadUnfavoriteSuccess,
  forumThreadsLockAll,
  forumThreadsLockAllFailure,
  forumThreadsLockAllSuccess,
  forumReactionOptionsLoad,
  forumReactionOptionsLoadFailure,
  forumReactionOptionsLoadSuccess,
  forumThreadsUnlockAll,
  forumThreadsUnlockAllFailure,
  forumThreadsUnlockAllSuccess,
} from "../actions/forum.actions";

export interface ForumState {
  threads: ForumThread[];
  threadsNext: number | null;
  threadDetails: ForumThread | null;
  postsByThreadId: Record<string, ForumPost[]>;
  postsNextByThreadId: Record<string, number | null>;
  attachmentsByPostId: Record<string, ForumAttachment[]>;
  attachmentsByThreadId: Record<string, ForumAttachment[]>;
  attachmentConfig: ForumAttachmentConfig | null;
  readsByThreadId: Record<string, ForumThreadRead[]>;
  reactionOptions: ForumReactionOption[];
  members: ForumMember[];
  overviewGroups: ForumOverviewGroup[];
  overviewGroupsNext: number | null;
  postReactionsByPostId: Record<string, ForumPostReaction[]>;
  loading: Record<string, boolean>;
  error: HttpErrorResponse | null;
}

const initialState: ForumState = {
  threads: [],
  threadsNext: 1,
  threadDetails: null,
  postsByThreadId: {},
  postsNextByThreadId: {},
  attachmentsByPostId: {},
  attachmentsByThreadId: {},
  attachmentConfig: null,
  readsByThreadId: {},
  reactionOptions: [],
  members: [],
  overviewGroups: [],
  overviewGroupsNext: 1,
  postReactionsByPostId: {},
  loading: {},
  error: null,
};

const setLoading = (
  loading: Record<string, boolean>,
  key: string,
  value: boolean,
): Record<string, boolean> => ({
  ...loading,
  [key]: value,
});

const withThreadPreview = (thread: ForumThread): ForumThread => ({
  ...thread,
  content_preview: thread.content_preview ?? thread.content ?? "",
});

const updatePostsByThreadId = (
  postsByThreadId: Record<string, ForumPost[]>,
  updater: (post: ForumPost) => ForumPost,
): Record<string, ForumPost[]> =>
  Object.keys(postsByThreadId).reduce<Record<string, ForumPost[]>>(
    (accumulator, threadId) => {
      accumulator[threadId] = postsByThreadId[threadId].map(updater);
      return accumulator;
    },
    {},
  );

const moveThreadToTop = (
  threads: ForumThread[],
  thread: ForumThread,
): ForumThread[] => [
  withThreadPreview(thread),
  ...threads.filter((item) => item?.id !== thread.id),
];

const moveThreadToBottom = (
  threads: ForumThread[],
  thread: ForumThread,
): ForumThread[] => [
  ...threads.filter((item) => item?.id !== thread.id),
  withThreadPreview(thread),
];

const insertThreadByDisplayOrder = (
  threads: ForumThread[],
  thread: ForumThread,
): ForumThread[] => {
  const nextThread = withThreadPreview(thread);
  const nextThreads = threads.filter((item) => item?.id !== thread.id);

  if (nextThread.is_pinned) {
    return [nextThread, ...nextThreads];
  }

  const insertIndex = nextThreads.findIndex((item) => {
    if (item.is_pinned) {
      return false;
    }

    if (nextThread.type === ForumThreadType.Announcement) {
      return item.type !== ForumThreadType.Announcement;
    }

    return item.type !== ForumThreadType.Announcement;
  });

  if (insertIndex === -1) {
    return [...nextThreads, nextThread];
  }

  return [
    ...nextThreads.slice(0, insertIndex),
    nextThread,
    ...nextThreads.slice(insertIndex),
  ];
};

const updateThreadInPlace = (
  thread: ForumThread | null,
  threadId: string,
  changes: Partial<ForumThread>,
): ForumThread | null => {
  if (!thread || thread.id !== threadId) {
    return thread;
  }

  return {
    ...thread,
    ...changes,
  };
};

const decrementThreadCommentsCount = (thread: ForumThread): ForumThread => ({
  ...thread,
  comments_count: Math.max((thread.comments_count ?? 0) - 1, 0),
});

const updateThreadReactionSummary = (
  thread: ForumThread | null,
  threadId: string,
  reactionsSummary: ForumReactionSummary[],
): ForumThread | null => {
  if (!thread || thread.id !== threadId) {
    return thread;
  }

  return {
    ...thread,
    reactions_summary: mergeReactionSummaries(
      thread.reactions_summary,
      reactionsSummary,
    ),
  };
};

const mergeReactionSummaries = (
  currentSummary: ForumReactionSummary[] | undefined,
  nextSummary: ForumReactionSummary[],
): ForumReactionSummary[] => {
  const currentSummaryMap = new Map(
    (currentSummary || []).map((reaction) => [reaction.emoji, reaction]),
  );

  return nextSummary.map((reaction) => ({
    ...reaction,
    reacted_by_me:
      reaction.reacted_by_me ??
      currentSummaryMap.get(reaction.emoji)?.reacted_by_me ??
      false,
  }));
};

const incrementThreadReactionSummary = (
  thread: ForumThread,
  emoji: string,
): ForumThread => {
  const reactionsSummary = thread.reactions_summary ?? [];
  const existingReaction = reactionsSummary.find(
    (reaction) => reaction.emoji === emoji,
  );

  return {
    ...thread,
    reactions_summary: existingReaction
      ? reactionsSummary.map((reaction) =>
          reaction.emoji === emoji
            ? {
                ...reaction,
                count: reaction.count + 1,
                reacted_by_me: true,
              }
            : reaction,
        )
      : [...reactionsSummary, { emoji, count: 1, reacted_by_me: true }],
  };
};

const decrementThreadReactionSummary = (
  thread: ForumThread,
  emoji: string,
): ForumThread => {
  const reactionsSummary = thread.reactions_summary ?? [];

  return {
    ...thread,
    reactions_summary: reactionsSummary
      .map((reaction) =>
        reaction.emoji === emoji
          ? {
              ...reaction,
              count: reaction.count - 1,
              reacted_by_me: false,
            }
          : reaction,
      )
      .filter((reaction) => reaction.count > 0),
  };
};

const incrementPostReactionSummary = (
  post: ForumPost,
  emoji: string,
): ForumPost => {
  const reactionsSummary = post.reactions_summary ?? [];
  const existingReaction = reactionsSummary.find(
    (reaction) => reaction.emoji === emoji,
  );

  return {
    ...post,
    reactions_summary: existingReaction
      ? reactionsSummary.map((reaction) =>
          reaction.emoji === emoji
            ? {
                ...reaction,
                count: reaction.count + 1,
                reacted_by_me: true,
              }
            : reaction,
        )
      : [...reactionsSummary, { emoji, count: 1, reacted_by_me: true }],
  };
};

const decrementPostReactionSummary = (
  post: ForumPost,
  emoji: string,
): ForumPost => {
  const reactionsSummary = post.reactions_summary ?? [];

  return {
    ...post,
    reactions_summary: reactionsSummary
      .map((reaction) =>
        reaction.emoji === emoji
          ? {
              ...reaction,
              count: reaction.count - 1,
              reacted_by_me: false,
            }
          : reaction,
      )
      .filter((reaction) => reaction.count > 0),
  };
};

const updatePostReactionSummary = (
  post: ForumPost,
  reactionsSummary: ForumReactionSummary[],
): ForumPost => ({
  ...post,
  reactions_summary: mergeReactionSummaries(
    post.reactions_summary,
    reactionsSummary,
  ),
});

export const forumReducer = createReducer(
  initialState,

  on(forumThreadsReset, (state): ForumState => ({
    ...state,
    threads: [],
    threadsNext: null,
  })),

  on(forumThreadDetailsLoad, (state): ForumState => ({
    ...state,
    threadDetails: null,
  })),

  on(
    forumThreadsLoad,
    forumThreadDetailsLoad,
    forumThreadCreate,
    forumThreadUpdate,
    forumThreadDelete,
    forumThreadDeletePernament,
    forumThreadRestore,
    forumThreadPin,
    forumThreadUnpin,
    forumPostsLoad,
    forumPostCreate,
    forumPostUpdate,
    forumPostDelete,
    forumAttachmentConfigLoad,
    forumAttachmentsLoad,
    forumAttachmentCreate,
    forumAttachmentDelete,
    forumThreadAttachmentsLoad,
    forumThreadAttachmentCreate,
    forumThreadAttachmentDelete,
    forumThreadReadsLoad,
    forumReactionOptionsLoad,
    forumMembersLoad,
    forumOverviewGroupsLoad,
    forumPostReactionsLoad,
    forumThreadReactionCreate,
    forumThreadReactionDelete,
    forumThreadReactionDetailsLoad,
    forumPostReactionCreate,
    forumPostReactionDelete,
    forumThreadFavorite,
    forumThreadUnfavorite,
    forumThreadsLockAll,
    forumThreadsUnlockAll,
    (state, action): ForumState => ({
      ...state,
      loading: setLoading(state.loading, action.type, true),
      error: null,
    }),
  ),

  on(
    forumAttachmentConfigLoadSuccess,
    (state, { config }): ForumState => ({
      ...state,
      attachmentConfig: config,
      loading: setLoading(
        state.loading,
        forumAttachmentConfigLoad.type,
        false,
      ),
    }),
  ),

  on(
    forumThreadsLoadSuccess,
    (state, { threads, next, page }): ForumState => ({
      ...state,
      threads:
        page > 1
          ? [
              ...state.threads,
              ...threads
                .map(withThreadPreview)
                .filter(
                  (thread) => !state.threads.some((item) => item.id === thread.id),
                ),
            ]
          : threads.map(withThreadPreview),
      threadsNext: next,
      loading: setLoading(state.loading, forumThreadsLoad.type, false),
    }),
  ),

  on(
    forumThreadDetailsLoadSuccess,
    (state, { thread }): ForumState => ({
      ...state,
      threadDetails: thread,
      loading: setLoading(state.loading, forumThreadDetailsLoad.type, false),
    }),
  ),

  on(
    forumThreadCreateSuccess,
    (state, { thread }): ForumState => ({
      ...state,
      threads: insertThreadByDisplayOrder(state.threads, thread),
      threadsNext: state.threadsNext,
      loading: setLoading(state.loading, forumThreadCreate.type, false),
    }),
  ),

  on(
    forumThreadUpdateSuccess,
    (state, { thread }): ForumState => ({
      ...state,
      threads: state.threads.map((item) =>
        item.id === thread.id ? withThreadPreview(thread) : item,
      ),
      threadDetails:
        state.threadDetails?.id === thread.id
          ? withThreadPreview(thread)
          : state.threadDetails,
      loading: setLoading(state.loading, forumThreadUpdate.type, false),
    }),
  ),

  on(
    forumThreadDeleteSuccess,
    (state, { threadId }): ForumState => ({
      ...state,
      threads: state.threads.filter((item) => item.id !== threadId),
      threadDetails:
        state.threadDetails?.id === threadId ? null : state.threadDetails,
      loading: setLoading(state.loading, forumThreadDelete.type, false),
    }),
  ),

  on(
    forumThreadDeletePernamentSuccess,
    (state, { threadId }): ForumState => ({
      ...state,
      threads: state.threads.filter((item) => item.id !== threadId),
      threadDetails:
        state.threadDetails?.id === threadId ? null : state.threadDetails,
      loading: setLoading(
        state.loading,
        forumThreadDeletePernament.type,
        false,
      ),
    }),
  ),

  on(
    forumThreadRestoreSuccess,
    (state, { thread }): ForumState => ({
      ...state,
      threads: state.threads.filter((item) => item.id !== thread.id),
      threadDetails:
        state.threadDetails?.id === thread.id
          ? withThreadPreview(thread)
          : state.threadDetails,
      loading: setLoading(state.loading, forumThreadRestore.type, false),
    }),
  ),

  on(
    forumThreadPinSuccess,
    (state, { thread }): ForumState => ({
      ...state,
      threads: moveThreadToTop(state.threads, thread),
      threadDetails:
        state.threadDetails?.id === thread.id
          ? withThreadPreview(thread)
          : state.threadDetails,
      loading: setLoading(state.loading, forumThreadPin.type, false),
    }),
  ),

  on(
    forumThreadUnpinSuccess,
    (state, { threadId }): ForumState => {
      const currentThread = state.threads.find((item) => item?.id === threadId);

      if (!currentThread) {
        return {
          ...state,
          loading: setLoading(state.loading, forumThreadUnpin.type, false),
        };
      }

      const unpinnedThread = withThreadPreview({
        ...currentThread,
        is_pinned: false,
        pinned_at: null,
      });

      return {
        ...state,
        threads: moveThreadToBottom(state.threads, unpinnedThread),
        threadDetails: updateThreadInPlace(state.threadDetails, threadId, {
          is_pinned: false,
          pinned_at: null,
        }),
        loading: setLoading(state.loading, forumThreadUnpin.type, false),
      };
    },
  ),

  on(
    forumPostsLoadSuccess,
    (state, { threadId, posts, next, page }): ForumState => {
      const currentPosts = state.postsByThreadId[threadId] || [];
      const nextPosts =
        page > 1
          ? [
              ...currentPosts,
              ...posts.filter(
                (post) => !currentPosts.some((item) => item.id === post.id),
              ),
            ]
          : posts;

      return {
        ...state,
        postsByThreadId: {
          ...state.postsByThreadId,
          [threadId]: nextPosts,
        },
        postsNextByThreadId: {
          ...state.postsNextByThreadId,
          [threadId]: next,
        },
        loading: setLoading(state.loading, forumPostsLoad.type, false),
      };
    },
  ),

  on(
    forumPostCreateSuccess,
    (state, { threadId, post, pendingAttachmentsCount = 0 }): ForumState => {
      const existingPosts = state.postsByThreadId[threadId] || [];
      const hasPost = existingPosts.some((item) => item.id === post.id);
      const nextPosts = hasPost
        ? existingPosts.map((item) =>
            item.id === post.id
              ? {
                  ...item,
                  ...post,
                  pending_attachments_count:
                    pendingAttachmentsCount ||
                    item.pending_attachments_count ||
                    0,
                }
              : item,
          )
        : [
            ...existingPosts,
            {
              ...post,
              pending_attachments_count: pendingAttachmentsCount,
            },
          ];

      return {
        ...state,
        postsByThreadId: {
          ...state.postsByThreadId,
          [threadId]: nextPosts,
        },
        threads: state.threads.map((thread) =>
          thread.id === threadId
            ? {
                ...thread,
                comments_count: hasPost
                  ? thread.comments_count
                  : (thread.comments_count ?? 0) + 1,
              }
            : thread,
        ),
        threadDetails:
          state.threadDetails?.id === threadId
            ? {
                ...state.threadDetails,
                comments_count: hasPost
                  ? state.threadDetails.comments_count
                  : (state.threadDetails.comments_count ?? 0) + 1,
              }
            : state.threadDetails,
        loading: setLoading(state.loading, forumPostCreate.type, false),
      };
    },
  ),

  on(
    forumPostUpdateSuccess,
    (state, { threadId, post }): ForumState => ({
      ...state,
      postsByThreadId: {
        ...state.postsByThreadId,
        [threadId]: (state.postsByThreadId[threadId] || []).map((item) =>
          item.id === post.id ? post : item,
        ),
      },
      loading: setLoading(state.loading, forumPostUpdate.type, false),
    }),
  ),

  on(
    forumPostDeleteSuccess,
    (state, { threadId, postId }): ForumState => {
      const existingPosts = state.postsByThreadId[threadId] || [];
      const hasPost = existingPosts.some((item) => item.id === postId);

      return {
        ...state,
        postsByThreadId: {
          ...state.postsByThreadId,
          [threadId]: existingPosts.filter((item) => item.id !== postId),
        },
        threads: hasPost
          ? state.threads.map((thread) =>
              thread.id === threadId
                ? decrementThreadCommentsCount(thread)
                : thread,
            )
          : state.threads,
        threadDetails:
          hasPost && state.threadDetails?.id === threadId
            ? decrementThreadCommentsCount(state.threadDetails)
            : state.threadDetails,
        loading: setLoading(state.loading, forumPostDelete.type, false),
      };
    },
  ),

  on(
    forumAttachmentsLoadSuccess,
    (state, { postId, attachments }): ForumState => ({
      ...state,
      attachmentsByPostId: {
        ...state.attachmentsByPostId,
        [postId]: attachments,
      },
      loading: setLoading(state.loading, forumAttachmentsLoad.type, false),
    }),
  ),

  on(
    forumAttachmentCreateSuccess,
    (state, { postId, attachment }): ForumState => ({
      ...state,
      postsByThreadId: updatePostsByThreadId(state.postsByThreadId, (post) =>
        post.id === postId
          ? {
              ...post,
              attachments: [...(post.attachments || []), attachment],
              pending_attachments_count: Math.max(
                0,
                (post.pending_attachments_count || 0) - 1,
              ),
            }
          : post,
      ),
      attachmentsByPostId: {
        ...state.attachmentsByPostId,
        [postId]: [...(state.attachmentsByPostId[postId] || []), attachment],
      },
      loading: setLoading(state.loading, forumAttachmentCreate.type, false),
    }),
  ),

  on(
    forumPostAttachmentsUploadFinished,
    (state, { threadId, postId }): ForumState => ({
      ...state,
      postsByThreadId: {
        ...state.postsByThreadId,
        [threadId]: (state.postsByThreadId[threadId] || []).map((post) =>
          post.id === postId
            ? {
                ...post,
                pending_attachments_count: 0,
              }
            : post,
        ),
      },
    }),
  ),

  on(
    forumAttachmentDeleteSuccess,
    (state, { postId, attachmentId }): ForumState => ({
      ...state,
      postsByThreadId: updatePostsByThreadId(state.postsByThreadId, (post) =>
        post.id === postId
          ? {
              ...post,
              attachments: (post.attachments || []).filter(
                (item) => item.id !== attachmentId,
              ),
            }
          : post,
      ),
      attachmentsByPostId: {
        ...state.attachmentsByPostId,
        [postId]: (state.attachmentsByPostId[postId] || []).filter(
          (item) => item.id !== attachmentId,
        ),
      },
      loading: setLoading(state.loading, forumAttachmentDelete.type, false),
    }),
  ),

  on(
    forumThreadAttachmentsLoadSuccess,
    (state, { threadId, attachments }): ForumState => ({
      ...state,
      attachmentsByThreadId: {
        ...state.attachmentsByThreadId,
        [threadId]: attachments,
      },
      loading: setLoading(
        state.loading,
        forumThreadAttachmentsLoad.type,
        false,
      ),
    }),
  ),

  on(
    forumThreadAttachmentCreateSuccess,
    (state, { threadId, attachment }): ForumState => ({
      ...state,
      threads: state.threads.map((thread) =>
        thread.id === threadId
          ? {
              ...thread,
              attachments: [...(thread.attachments || []), attachment],
            }
          : thread,
      ),
      threadDetails:
        state.threadDetails?.id === threadId
          ? {
              ...state.threadDetails,
              attachments: [...(state.threadDetails.attachments || []), attachment],
            }
          : state.threadDetails,
      attachmentsByThreadId: {
        ...state.attachmentsByThreadId,
        [threadId]: [
          ...(state.attachmentsByThreadId[threadId] || []),
          attachment,
        ],
      },
      loading: setLoading(
        state.loading,
        forumThreadAttachmentCreate.type,
        false,
      ),
    }),
  ),

  on(
    forumThreadAttachmentDeleteSuccess,
    (state, { threadId, attachmentId }): ForumState => ({
      ...state,
      threads: state.threads.map((thread) =>
        thread.id === threadId
          ? {
              ...thread,
              attachments: (thread.attachments || []).filter(
                (item) => item.id !== attachmentId,
              ),
            }
          : thread,
      ),
      threadDetails:
        state.threadDetails?.id === threadId
          ? {
              ...state.threadDetails,
              attachments: (state.threadDetails.attachments || []).filter(
                (item) => item.id !== attachmentId,
              ),
            }
          : state.threadDetails,
      attachmentsByThreadId: {
        ...state.attachmentsByThreadId,
        [threadId]: (state.attachmentsByThreadId[threadId] || []).filter(
          (item) => item.id !== attachmentId,
        ),
      },
      loading: setLoading(
        state.loading,
        forumThreadAttachmentDelete.type,
        false,
      ),
    }),
  ),

  on(
    forumThreadReadsLoadSuccess,
    (state, { threadId, reads }): ForumState => ({
      ...state,
      readsByThreadId: {
        ...state.readsByThreadId,
        [threadId]: reads,
      },
      loading: setLoading(state.loading, forumThreadReadsLoad.type, false),
    }),
  ),

  on(
    forumReactionOptionsLoadSuccess,
    (state, { options }): ForumState => ({
      ...state,
      reactionOptions: options,
      loading: setLoading(state.loading, forumReactionOptionsLoad.type, false),
    }),
  ),

  on(
    forumMembersLoadSuccess,
    (state, { members }): ForumState => ({
      ...state,
      members,
      loading: setLoading(state.loading, forumMembersLoad.type, false),
    }),
  ),

  on(
    forumOverviewGroupsLoadSuccess,
    (state, { groups, next, page }): ForumState => ({
      ...state,
      overviewGroups: page > 1 ? [...state.overviewGroups, ...groups] : groups,
      overviewGroupsNext: next,
      loading: setLoading(state.loading, forumOverviewGroupsLoad.type, false),
    }),
  ),

  on(
    forumPostReactionsLoadSuccess,
    (state, { postId, reactions }): ForumState => ({
      ...state,
      postReactionsByPostId: {
        ...state.postReactionsByPostId,
        [postId]: reactions,
      },
      loading: setLoading(state.loading, forumPostReactionsLoad.type, false),
    }),
  ),

  on(
    forumThreadReactionCreateSuccess,
    (state, { threadId, reaction }): ForumState => ({
      ...state,
      threads: state.threads.map((thread) =>
        thread.id === threadId
          ? incrementThreadReactionSummary(thread, reaction.emoji)
          : thread,
      ),
      threadDetails:
        state.threadDetails?.id === threadId
          ? incrementThreadReactionSummary(state.threadDetails, reaction.emoji)
          : state.threadDetails,
      loading: setLoading(state.loading, forumThreadReactionCreate.type, false),
    }),
  ),

  on(
    forumThreadReactionDeleteSuccess,
    (state, { threadId, emoji }): ForumState => ({
      ...state,
      threads: state.threads.map((thread) =>
        thread.id === threadId
          ? decrementThreadReactionSummary(thread, emoji)
          : thread,
      ),
      threadDetails:
        state.threadDetails?.id === threadId
          ? decrementThreadReactionSummary(state.threadDetails, emoji)
          : state.threadDetails,
      loading: setLoading(state.loading, forumThreadReactionDelete.type, false),
    }),
  ),

  on(
    forumThreadReactionChanged,
    (state, { threadId, reactionsSummary }): ForumState => ({
      ...state,
      threads: state.threads.map((thread) =>
        thread.id === threadId
          ? {
              ...thread,
              reactions_summary: mergeReactionSummaries(
                thread.reactions_summary,
                reactionsSummary,
              ),
            }
          : thread,
      ),
      threadDetails: updateThreadReactionSummary(
        state.threadDetails,
        threadId,
        reactionsSummary,
      ),
    }),
  ),

  on(
    forumThreadReactionDetailsLoadSuccess,
    (state): ForumState => ({
      ...state,
      loading: setLoading(
        state.loading,
        forumThreadReactionDetailsLoad.type,
        false,
      ),
    }),
  ),

  on(
    forumPostReactionCreateSuccess,
    (state, { threadId, postId, emoji }): ForumState => ({
      ...state,
      postsByThreadId: {
        ...state.postsByThreadId,
        [threadId]: (state.postsByThreadId[threadId] ?? []).map((post) =>
          post.id === postId ? incrementPostReactionSummary(post, emoji) : post,
        ),
      },
      loading: setLoading(state.loading, forumPostReactionCreate.type, false),
    }),
  ),

  on(
    forumPostReactionDeleteSuccess,
    (state, { threadId, postId, emoji }): ForumState => ({
      ...state,
      postsByThreadId: {
        ...state.postsByThreadId,
        [threadId]: (state.postsByThreadId[threadId] ?? []).map((post) =>
          post.id === postId ? decrementPostReactionSummary(post, emoji) : post,
        ),
      },
      loading: setLoading(state.loading, forumPostReactionDelete.type, false),
    }),
  ),

  on(
    forumPostReactionChanged,
    (state, { threadId, postId, reactionsSummary }): ForumState => ({
      ...state,
      postsByThreadId: {
        ...state.postsByThreadId,
        [threadId]: (state.postsByThreadId[threadId] ?? []).map((post) =>
          post.id === postId
            ? updatePostReactionSummary(post, reactionsSummary)
            : post,
        ),
      },
    }),
  ),

  on(
    forumThreadFavoriteSuccess,
    (state, { threadId }): ForumState => ({
      ...state,
      threads: state.threads.map((item) =>
        item.id === threadId ? { ...item, is_favorite: true } : item,
      ),
      threadDetails:
        state.threadDetails?.id === threadId
          ? { ...state.threadDetails, is_favorite: true }
          : state.threadDetails,
      loading: setLoading(state.loading, forumThreadFavorite.type, false),
    }),
  ),

  on(
    forumThreadUnfavoriteSuccess,
    (state, { threadId }): ForumState => ({
      ...state,
      threads: state.threads.map((item) =>
        item.id === threadId ? { ...item, is_favorite: false } : item,
      ),
      threadDetails:
        state.threadDetails?.id === threadId
          ? { ...state.threadDetails, is_favorite: false }
          : state.threadDetails,
      loading: setLoading(state.loading, forumThreadUnfavorite.type, false),
    }),
  ),

  on(
    forumThreadsLockAllSuccess,
    (state): ForumState => ({
      ...state,
      threads: state.threads.map((item) => ({ ...item, is_locked: true })),
      threadDetails: state.threadDetails
        ? { ...state.threadDetails, is_locked: true }
        : state.threadDetails,
      loading: setLoading(state.loading, forumThreadsLockAll.type, false),
    }),
  ),

  on(
    forumThreadsUnlockAllSuccess,
    (state): ForumState => ({
      ...state,
      threads: state.threads.map((item) => ({ ...item, is_locked: false })),
      threadDetails: state.threadDetails
        ? { ...state.threadDetails, is_locked: false }
        : state.threadDetails,
      loading: setLoading(state.loading, forumThreadsUnlockAll.type, false),
    }),
  ),

  on(
    forumThreadsLoadFailure,
    forumThreadDetailsLoadFailure,
    forumThreadCreateFailure,
    forumThreadUpdateFailure,
    forumThreadDeleteFailure,
    forumThreadDeletePernamentFailure,
    forumThreadRestoreFailure,
    forumThreadPinFailure,
    forumThreadUnpinFailure,
    forumPostsLoadFailure,
    forumPostCreateFailure,
    forumPostUpdateFailure,
    forumPostDeleteFailure,
    forumAttachmentConfigLoadFailure,
    forumAttachmentsLoadFailure,
    forumAttachmentCreateFailure,
    forumAttachmentDeleteFailure,
    forumThreadAttachmentsLoadFailure,
    forumThreadAttachmentCreateFailure,
    forumThreadAttachmentDeleteFailure,
    forumThreadReadsLoadFailure,
    forumReactionOptionsLoadFailure,
    forumMembersLoadFailure,
    forumOverviewGroupsLoadFailure,
    forumPostReactionsLoadFailure,
    forumThreadReactionCreateFailure,
    forumThreadReactionDeleteFailure,
    forumThreadReactionDetailsLoadFailure,
    forumPostReactionCreateFailure,
    forumPostReactionDeleteFailure,
    forumThreadFavoriteFailure,
    forumThreadUnfavoriteFailure,
    forumThreadsLockAllFailure,
    forumThreadsUnlockAllFailure,
    (state, { error, type }): ForumState => ({
      ...state,
      loading: setLoading(state.loading, type.replace(" Failure", ""), false),
      error,
    }),
  ),
);
